import { useEffect, useState } from 'react';
import _ from 'lodash';
import { useFormik } from 'formik';
import { DefaultRdpManager, RdpManager } from 'core/rdp/RdpManager';
import { useCallAPI } from 'hooks/useCallAPI';
import { useCampaignStore } from 'store/campaignStore';
import { useProductStore } from 'store/productStore';
import { BidMode, RoasStrategy } from 'core/rtbCampaign/singlePage/keyword';
import { BudgetType, ActionType } from 'core/rtbCampaign/singlePage/common';
import { Product, ProductWithPagination } from 'core/product/Product';
import moment from 'moment';
import { ProductFilter } from 'containers/RetailMomo/Products/ProductFilter';

interface OutboundKeywordForm {
  defaultForm: any;
  values: ReturnType<typeof useFormik>['values'];
  setFieldValue: ReturnType<typeof useFormik>['setFieldValue'];
  filters?: ProductFilter;
}

export enum Goal {
  LINK_CLICKS = 'LINK_CLICKS',
  CONVERSIONS = 'CONVERSIONS',
}

export interface OutboundKeywordInfo {
  brandNo: string;
  brandName: string;
  name: string;
  logoUrl: string;
  adContent: string;
  segment: string[];
  goal: Goal;
}

const defaultRdpManager: RdpManager = new DefaultRdpManager();

/**
 *
 * @param initialState
 * @returns
 *
 */
const useOutboundKeywordForm = (
  initialState: OutboundKeywordForm = {
    defaultForm: {},
    values: {},
    setFieldValue: async () => Promise.resolve(),
    filters: undefined,
  },
) => {
  const { defaultForm, values, setFieldValue, filters } = initialState;

  const { callAPIs } = useCallAPI();

  const { actionType, handleLoading, handleSetRoasList, roasList } =
    useCampaignStore();

  const { liveProducts, selectedAutoProducts, handleSetProductList } =
    useProductStore();

  // 活動期間
  const [diffDays, setDiffDays] = useState(1);

  const handleDateRangeChange = (startDate, endDate) => {
    setFieldValue('basic.startDate', startDate);
    setFieldValue('basic.endDate', endDate);
  };

  const handleChangeStartDate = (startDate) => {
    setFieldValue('basic.startDate', startDate);
  };

  const handleChangeEndDate = (endDate) => {
    setFieldValue('basic.endDate', endDate);
  };

  // 變更預算類型
  const handleBudgetTypeChange = (newBudgetType) => {
    setFieldValue('basic.budgetType', newBudgetType);
    handleDResetAllBudget();
  };
  // 變更預算
  const handleBudgetChange = (newBudget) => {
    const { type, newPrice } = newBudget;
    if (type === BudgetType.DAILY) {
      setFieldValue('basic.dailyTargetBudget', newPrice);
      // 編輯模式下日預算模式有異動日預算才執行 handleAutoSetBudget
      if (
        actionType === ActionType.EDIT &&
        Number(newPrice) === defaultForm.basic.dailyTargetBudget
      ) {
        setFieldValue('basic.budget', defaultForm.basic.budget);
      } else {
        handleAutoSetBudget(newPrice);
      }
    } else if (type === BudgetType.TOTAL) {
      setFieldValue('basic.budget', newPrice);
      handleAutoSetDailyBudget(newPrice);
    }
  };
  // 自動設定每日預算
  const handleAutoSetDailyBudget = (newBudget) => {
    if (values.basic.budgetType === BudgetType.TOTAL) {
      setFieldValue('basic.dailyTargetBudget', Number(newBudget).toFixed(2));
    }
  };
  // 自動設定總預算
  const handleAutoSetBudget = (newDailyBudget) => {
    const { basic } = values;
    const start = moment(basic.startDate);
    const end = moment(basic.endDate);
    const today = moment();

    // 根據狀態選擇計算天數的基準
    const baseDate =
      actionType === ActionType.CREATE
        ? start
        : start.isAfter(today)
          ? start
          : today;

    // +1 是因為同日也算 1 天
    const days = end.diff(baseDate, 'days') + 1;

    const { budgetType, expectedSpent } = values.basic;
    if (budgetType === BudgetType.DAILY) {
      const daily = Number(newDailyBudget) || 0;
      const added = daily * days;

      const total =
        Math.round((expectedSpent + added + Number.EPSILON) * 100) / 100;
      setFieldValue('basic.budget', total.toFixed(2));
    }
  };
  // 重置所有預算
  const handleDResetAllBudget = () => {
    setFieldValue('basic.budget', '0');
    setFieldValue('basic.dailyTargetBudget', '0');
  };

  // 根據已選擇的品牌取得商品
  const fetchProductsToGetRoas = async (
    productIds: string[],
  ): Promise<void> => {
    if (!_.isEmpty(filters) && !_.isNil(filters.advertiser)) {
      const retailId = _.get(filters, 'retailerId', '');
      // const queries = handleSearchQueries(filters, categories);
      if (_.isEmpty(retailId)) {
        return;
      }
      try {
        const pageable = { page: 1, sizePerPage: 25 };
        handleSetProductList([]);
        await callAPIs(
          [
            defaultRdpManager.getRetailProducts.bind(
              defaultRdpManager,
              retailId,
              pageable,
              {
                productIds: productIds,
              },
            ),
          ],
          (productWithPagination: ProductWithPagination) => {
            handleSetProductList(productWithPagination.products);
          },
        );
      } catch (error) {
        console.error('Failed to fetch data:', error);
      } finally {
        handleLoading(false);
      }
    }
  };

  // 切換出價選擇
  const handleBidTypeChange = (newBidMode) => {
    setFieldValue('basic.bidMode', newBidMode);
    setFieldValue('basic.roasOption.roas', null);
    setFieldValue('basic.roasOption.roasStrategy', RoasStrategy.HIGH_ROAS);
  };

  // 切換ROAS策略
  const handleRoasStrategyChange = (newAutoMode) => {
    setFieldValue('basic.roasOption.roasStrategy', newAutoMode);
    setFieldValue('basic.roasOption.roas', null);
  };

  // 自動出價-自訂 ROAS 修改
  const handleCustomRoasChange = (newCustomRoas) => {
    setFieldValue('basic.roasOption.roas', newCustomRoas);
  };

  // 取得 自動出價 ROAS 價格
  const handleGetRoasPrice = (products: Product[]) => {
    try {
      // ROAS目標參數
      const roasList = [0.8, 1, 1.2];
      // 根據各商品歷史ROAS*ROAS目標參數*各商品花費權重加總計算出活動層級的預估ROAS
      const finalRoasList = roasList.map((roas) => {
        const totalPrice = products.reduce((acc, { productId, spending }) => {
          const liveProduct = liveProducts?.find(
            (liveProduct) => liveProduct.productId === productId,
          );
          const liveSpending = liveProduct?.spending ?? spending;
          return acc + liveSpending;
        }, 0);
        const totalRoas = products
          .reduce((acc, { productId, customLabel4, spending }) => {
            const liveProduct = liveProducts?.find(
              (liveProduct) => liveProduct.productId === productId,
            );
            const liveRoas = liveProduct?.customLabel4 ?? customLabel4;
            const liveSpending = liveProduct?.spending ?? spending;
            const weight = liveSpending / totalPrice;
            return acc + Number(liveRoas) * roas * weight;
          }, 0)
          .toFixed(2);
        return totalRoas === '0.00' ? '-' : totalRoas;
      });
      handleSetRoasList(finalRoasList);
      return finalRoasList;
    } catch (error) {
      console.error('Error calculating ROAS price:', error);
    }
  };

  const handleSegmentChange = (segment: string[]) => {
    const filteredSegment = segment.filter((s) => s !== 'categoryRoot');
    setFieldValue('basic.outboundInfo.segment', filteredSegment);
  };

  const handleGoalChange = (goal: Goal) => {
    setFieldValue('basic.outboundInfo.goal', goal);
  };

  const handleCustomRoasAndInferGoal = (roasStr: string) => {
    //  更新 自訂 roas 數值 跟 切換 rosaStrategy
    handleCustomRoasChange(roasStr);
    if (values?.basic?.roasOption?.roasStrategy !== RoasStrategy.CUSTOM_ROAS)
      return;

    //  根據 roasList 自動判斷目標
    const threshold = parseFloat(roasList[0]);
    // 自訂 roas 數值
    const inputValue = parseFloat(roasStr);

    setFieldValue(
      'basic.outboundInfo.goal',
      inputValue <= threshold ? Goal.LINK_CLICKS : Goal.CONVERSIONS,
    );
  };

  useEffect(() => {
    // setFieldValue('products', selectedProducts);
    setFieldValue('autoProducts', selectedAutoProducts);
  }, [selectedAutoProducts, setFieldValue]);

  useEffect(() => {
    handleGetRoasPrice(selectedAutoProducts);
  }, [selectedAutoProducts, values.basic.selectPattern, liveProducts]);

  useEffect(() => {
    if (
      (actionType === ActionType.EDIT || actionType === ActionType.COPY) &&
      !_.isEmpty(filters) &&
      defaultForm.basic.bidMode === BidMode.AUTO
    ) {
      fetchProductsToGetRoas(selectedAutoProducts.map((p) => p.productId));
    }
  }, [filters, defaultForm.basic.bidMode, defaultForm.basic.selectPattern]);

  // 取得活動期間天數
  useEffect(() => {
    const startDate = moment(values?.basic.startDate);
    const endDate = moment(values?.basic.endDate);
    const defaultStartDate = moment(defaultForm?.basic.startDate);
    const defaultEndDate = moment(defaultForm?.basic.endDate);
    // 需要 +1 是因為當 startDate & endDate 都是同一天時 moment.js 會判定 diff 為 0,
    // 但這邊業務需求會需要是1天
    const diffDays = endDate.diff(startDate, 'days') + 1;
    setDiffDays(diffDays);
    // 編輯模式下日預算模式有異動開始與結束日期才執行 handleRunnungPatternAutoSetBudget
    if (values.basic.budgetType === BudgetType.DAILY) {
      if (
        actionType === ActionType.EDIT &&
        startDate.isSame(defaultStartDate) &&
        endDate.isSame(defaultEndDate) &&
        defaultForm.basic.dailyTargetBudget?.toString() ===
          values.basic.dailyTargetBudget?.toString()
      ) {
        setFieldValue('basic.budget', defaultForm.basic.budget);
      } else {
        handleAutoSetBudget(values.basic.dailyTargetBudget);
      }
    }
  }, [
    values.basic.budgetType,
    values.basic.startDate,
    values.basic.endDate,
    values.basic.dailyTargetBudget,
  ]);
  return {
    diffDays,
    handleDateRangeChange,
    handleChangeStartDate,
    handleChangeEndDate,
    handleBudgetTypeChange,
    handleBudgetChange,
    selectedAutoProducts,
    handleBidTypeChange,
    handleRoasStrategyChange,
    handleCustomRoasChange,
    handleCustomRoasAndInferGoal,
    handleSegmentChange,
    handleGoalChange,
  };
};

export default useOutboundKeywordForm;
